<template>
  <v-menu
    v-show="vMenuContentLoaded"
    v-bind="$attrs"
    v-model="isOpened"
    :data-testid="sectionTag ? `${sectionTag}-menu` : undefined"
    :close-on-content-click="true"
    :location="location"
    :offset="menuOffset"
    content-class="wasabi__flyout"
    :attach="props.attach"
    scroll-strategy="close"
  >
    <template #activator="{ props: activatorProps }">
      <div ref="baseElement" class="baseElement">
        <slot name="activator" :props="activatorProps">
          <WButton
            v-bind="activatorProps"
            :section-tag="sectionTag ? `${sectionTag}-flyout` : undefined"
            variant="icon"
            :icon="dotsIcon"
            :label="$t('app.common.actions.moreActions')"
            :class="{ active: isIconClicked }"
            :color="isOpened ? tokens.color.content.text.secondary : tokens.color.action.primary.surface"
            :icon-border="bordered"
            :inside-component="insideComponent"
            :disabled="props.disabled"
            @click="toggleIconClicked"
          />
        </slot>
      </div>
    </template>

    <div id="flyout" ref="flyout">
      <div ref="arrow" class="flyout-arrow" :class="arrowPosition" :style="arrowStyle"></div>

      <div class="button-container">
        <v-list color="primary">
          <v-list-item
            v-for="(item, index) in components"
            :key="getKey(item, index)"
            :data-testid="sectionTag ? `${sectionTag}-${slugify(item.title)}-item` : undefined"
            :value="index"
            :class="{ 'flyout-header': item.header }"
            :disabled="item.header || item.disabled"
            @click="itemClicked(item)"
          >
            <slot name="item-renderer" :props="{item, index}">
              <v-list-item-title
                :data-testid="sectionTag ? `${sectionTag}-${slugify(item.title)}-title` : undefined"
              >
                <v-icon
                  v-if="hasIcons && hasIcon(item)"
                  :data-testid="sectionTag ? `${sectionTag}-${slugify(item.title)}-icon` : undefined"
                  :icon="item.icon"
                  class="mr-2"
                ></v-icon>
                <span v-else-if="hasIcons && !hasIcon(item)" class="missing-icon-spacer"></span>
                {{ item.title }}
              </v-list-item-title>
            </slot>
          </v-list-item>
        </v-list>
      </div>
    </div>
  </v-menu>
</template>

<script setup lang="ts">
import { mdiDotsVertical, mdiDotsHorizontal } from '@mdi/js'
import { ref, onMounted, onUnmounted, watch, onUpdated, computed, nextTick } from 'vue'

import { $t } from '@/plugins/i18n'
import { tokens } from '@/plugins/vuetify'
import { WButton } from '@wasabi/actions'
import { slugify } from '@/composables/useDataTestid'
import type { Component } from '@/types/Flyout'
import { type Anchor, Anchors } from '@/types/Anchor'

export interface WFlyoutProps {
  sectionTag?: string
  position: string
  modalData?: boolean | null
  components?: Component[]
  parentItem?: Record<string, unknown> | null
  bordered?: boolean
  /** Overlay attach target. Configurable so consumers can override the default
      (`.v-application`) when attaching the overlay forces a full re-render on certain pages. */
  attach?: string
  disabled?: boolean
  orientation?: 'vertical' | 'horizontal'
  /** Shrinks the dots activator to WButton's compact 22px "inside other components" sizing. */
  insideComponent?: boolean
}

const props = withDefaults(defineProps<WFlyoutProps>(), {
  sectionTag: 'unspecified',
  position: 'top',
  modalData: null,
  components: () => [],
  parentItem: null,
  bordered: false,
  // ADX override, see scripts/sync-wasabi/config.json (upstream: '.v-application').
  attach: 'vue-component-wrapper',
  disabled: false,
  orientation: 'vertical',
  insideComponent: false,
})

const dotsIcon = computed(() => props.orientation === 'horizontal' ? mdiDotsHorizontal : mdiDotsVertical)

const defaultBorderWidth: number = 13

const baseElement = ref<HTMLElement | null>(null)
const flyout = ref<HTMLElement | null>(null)
const arrow = ref<HTMLElement | null>(null)
const arrowPosition = ref<string>(props.position)
const isIconClicked = ref<boolean>(false)
const isOpened = ref<boolean>(false)
const vMenuContentLoaded = ref<boolean>(false)
const borderWidth = ref<number>(0)
const color = ref<string>('transparent')
const menuOffset = ref<number>(20)

const emit = defineEmits(['click-outside', 'flyout-open', 'item-clicked'])

onMounted(() => {
  vMenuContentLoaded.value = false
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  vMenuContentLoaded.value = false
  window.removeEventListener('resize', handleResize)
})

onUpdated(() => {
  if (vMenuContentLoaded.value) {
    recalculateArrowDirection()
    nextTick(() => {
      recalculateArrowPosition()
    })
    color.value = tokens.color.background.surface.default
    borderWidth.value = defaultBorderWidth
  } else {
    borderWidth.value = 0
    color.value = 'transparent'
  }
})

watch(isOpened, (newVal) => {
  if (newVal) {
    showFlyout()
  } else {
    hideFlyout()
  }
})

const arrowStyle = computed(() => {
  return {
    border: `${borderWidth.value}px solid ${color.value}`
  }
})

const location = computed((): Anchor => {
  switch (props.position) {
    case 'top':
      return Anchors.TopCenter
    case 'bottom':
      return Anchors.BottomCenter
    case 'right':
      return Anchors.RightCenter
    case 'left':
    default:
      return Anchors.LeftCenter
  }
})

const hasIcons = computed((): boolean => props.components.some((item: any) => item.icon))

const showFlyout = () => {
  nextTick(() => {
    vMenuContentLoaded.value = true
    emit('flyout-open')
  })
}
const hideFlyout = () => {
  vMenuContentLoaded.value = false
}

const recalculateArrowPosition = () => {
  if (baseElement.value === null || flyout.value === null) {
    defaultArrowPosition()
    return
  }

  try {
    const baseElementRect = baseElement.value.getBoundingClientRect()
    const flyOutRect = flyout.value.getBoundingClientRect()

    if (arrowPosition.value === 'left' || arrowPosition.value === 'right') {
      const verticalCenterBase = baseElementRect.top + baseElementRect.height / 2 - flyOutRect.top
      const verticalCenterMax = flyOutRect.height - defaultBorderWidth - 6
      const verticalCenterMin = defaultBorderWidth + 6
      const verticalCenter = Math.max(Math.min(verticalCenterBase, verticalCenterMax), verticalCenterMin)
      arrow.value!.style.top = `calc(${verticalCenter}px - ${defaultBorderWidth}px)`
    } else {
      const horizontalCenterBase = baseElementRect.left + baseElementRect.width / 2 - flyOutRect.left
      const horizontalCenterMax = flyOutRect.width - defaultBorderWidth - 6
      const horizontalCenterMin = defaultBorderWidth + 6
      const horizontalCenter = Math.max(Math.min(horizontalCenterBase, horizontalCenterMax), horizontalCenterMin)
      arrow.value!.style.left = `calc(${horizontalCenter}px)`
    }
  } catch {
    defaultArrowPosition()
  }
}

const defaultArrowPosition = (): void => {
  if (arrowPosition.value === 'left' || arrowPosition.value === 'right') {
    arrow.value!.style.top = `calc(50% - ${defaultBorderWidth}px)`
  } else {
    arrow.value!.style.left = `calc(50%)`
  }
}

const recalculateArrowDirection = (): void => {
  const flyout: HTMLElement = document.querySelector('.wasabi__flyout') as HTMLElement
  const computedStyles: CSSStyleDeclaration = getComputedStyle(flyout)
  const anchorOrigin: string = computedStyles.getPropertyValue('--v-overlay-anchor-origin')

  if (arrowPosition.value == 'right' && anchorOrigin.includes('left')) {
    arrowPosition.value = 'left'
  } else if (arrowPosition.value == 'left' && anchorOrigin.includes('right')) {
    arrowPosition.value = 'right'
  } else if (arrowPosition.value == 'bottom' && anchorOrigin.includes('top')) {
    arrowPosition.value = 'top'
  } else if (arrowPosition.value == 'top' && anchorOrigin.includes('bottom')) {
    arrowPosition.value = 'bottom'
  }
}

const getKey = (item: Component, index: number): PropertyKey => (item.key !== undefined ? item.key : index)

const hasIcon = (item: Component): boolean => item.icon !== undefined

const itemClicked = (item: Component) => {
  if (props.parentItem) {
    emit('item-clicked', item, props.parentItem)
  } else {
    emit('item-clicked', item)
  }
  // force close on click for mobile
  isOpened.value = false
}

const handleResize = () => {
  if (vMenuContentLoaded.value) {
    recalculateArrowPosition()
    recalculateArrowDirection()
  }
}

const toggleIconClicked = () => {
  isIconClicked.value = !isIconClicked.value
}
</script>

<style lang="scss" scoped>
#flyout {
  @include tag(label-semibold-small);
  position: relative;
  display: flex;
  min-width: 108px;
  min-height: fit-content;
  height: fit-content;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 8px;
  line-height: 18px;
  border-radius: 4px;
  background: var(--color-background-surface-default);
  color: var(--color-content-text-primary);
  box-shadow: 0px 2px 7px 0px rgba(73, 73, 73, 0.25);
  cursor: pointer;
}

.wasabi__flyout .button-container {
  width: 100%;
  height: 100%;
  padding: 0;

  .v-list {
    padding: 0;
    gap: 8px;
  }

  :deep(.v-list-item) {
    padding: 8px;
    border-radius: 4px;
    min-height: unset !important;
  }

  :deep(.v-list-item-title) {
    @include tag(label-semibold-small);
    margin: 0 !important;
  }

  :deep(.v-list-item):hover {
    background-color: var(--color-background-surface-hover);
    color: var(--color-content-text-link);
  }
  
  :deep(.flyout-header) {
    background-color: var(--color-background-surface-secondary) !important;
    color: var(--color-content-text-secondary) !important;
    cursor: default !important;
    pointer-events: none !important;
  }

  :deep(.flyout-header):hover {
    background-color: var(--color-background-surface-secondary) !important;
    color: var(--color-content-text-secondary) !important;
  }
}

.wasabi__flyout .flyout-arrow {
  content: '';
  position: absolute;
  width: 0;
  height: 0;
  box-sizing: border-box;
  box-shadow: -3px 5px 5px 0 var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2));
}

.wasabi__flyout .top {
  margin-left: -19px;
  bottom: -25px;
  left: 50%;
  border-color: transparent transparent var(--color-background-surface-default) var(--color-background-surface-default);
  transform-origin: 0 0;
  transform: rotate(-45deg);
}

.wasabi__flyout .bottom {
  top: -17px;
  left: 50%;
  border-color: var(--color-background-surface-default) transparent transparent var(--color-background-surface-default);
  transform-origin: 0 0;
  transform: rotate(45deg);
  box-shadow: -3px -5px 5px 0 var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2));
}

.wasabi__flyout .right {
  margin-left: -17px;
  left: 50%;
  border-color: transparent transparent var(--color-background-surface-default) var(--color-background-surface-default);
  transform: rotate(45deg);
  left: 5px !important;
}

.wasabi__flyout .left {
  margin-left: -14px;
  bottom: -28px;
  border-color: transparent transparent var(--color-background-surface-default) var(--color-background-surface-default) !important;
  transform: rotate(225deg);
  left: 100% !important;
}

.baseElement {
  display: inline-flex;
  justify-content: center;
  align-items: center;
}

.missing-icon-spacer {
  display: inline-block;
  width: 20px;
  height: 20px;
  margin-right: 8px;
}
</style>
